import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';
import {
  getListingPriceVisibility,
  isPriceHidden,
  PRICE_VISIBILITY_VISIBLE,
} from '../../../util/userHelpers';

import { PriceLocked } from '../../../components';
import { getPriceLockedMessageId } from '../../../components/PriceLocked/PriceLocked';

import css from './SearchMapPriceLabel.module.css';

/**
 * SearchMapPriceLabel component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.listing} props.listing - The listing
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {Object} props.config - The configuration
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    // The viewer's own profile is fetched after the map first renders, so the
    // marker must re-render when the verified-users-only price rule resolves.
    const hasSamePriceVisibility = this.props.priceVisibility === nextProps.priceVisibility;

    return !(
      isSameListing &&
      hasSamePrice &&
      hasSameActiveStatus &&
      hasSameRefreshToken &&
      hasSamePriceVisibility
    );
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
      priceVisibility = PRICE_VISIBILITY_VISIBLE,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price, publicData, title } = currentListing.attributes;

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice =
      price && price.currency === config.currency
        ? formatMoney(intl, price)
        : price?.currency
        ? price.currency
        : null;

    const priceValue = formattedPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.price' }, { priceValue: formattedPrice })
      : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = hasMultiplePriceVariants
      ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue })
      : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue });

    // Prices are for verified users only. A blocked viewer gets a padlock marker
    // instead of the price; while their own profile is still loading the marker
    // stays blank so a verified user never sees the padlock flash. Either way the
    // marker itself remains, so the map still shows where the listings are.
    const hidePrice = isPriceHidden(priceVisibility);
    const showCompactMarker = priceVisibility !== PRICE_VISIBILITY_VISIBLE;

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice && !showCompactMarker,
      [css.lockedPriceLabel]: showCompactMarker,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    // The marker is a padlock, so screen readers get the reason spelled out —
    // reusing PriceLocked's own wording so the two never drift apart.
    const ariaLabel = hidePrice
      ? intl.formatMessage(
          { id: 'SearchMapPriceLabel.screenreader.mapMarkerPriceHidden' },
          { reason: intl.formatMessage({ id: getPriceLockedMessageId(priceVisibility) }) }
        )
      : priceValue
      ? priceMessage
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() => onListingClicked(currentListing)}
        aria-label={ariaLabel}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>
          {showCompactMarker ? (
            hidePrice ? (
              <PriceLocked
                rootClassName={css.priceLockedInLabel}
                reason={priceVisibility}
                iconOnly
              />
            ) : null
          ) : (
            priceMessage
          )}
        </div>
        <div className={caretClasses} />
      </button>
    );
  }
}

// The marker is rendered through a portal from the map overlay, so it still sits
// inside the app's redux Provider and can read the viewer's approval state.
const mapStateToProps = state => ({
  priceVisibility: getListingPriceVisibility(state.user.currentUser, state.auth.isAuthenticated),
});

export default connect(mapStateToProps)(injectIntl(SearchMapPriceLabel));
